import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink, updatePassword } from 'firebase/auth';
import { FaCheck, FaEnvelopeOpenText, FaEye, FaEyeSlash, FaLock, FaSpinner } from 'react-icons/fa';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { customToast } from '../utils/customToast';
import { activateFamilyPilot, loadPendingActivationPayload } from '../services/paymentsApi';
import { clearSignup, clearSignupDraft, claimAdminInvite, decodePayloadToken, loadSignup, loadSignupDraft, provisionSelfServeOrg, SelfServePayload } from './selfServeSignup';
import '../site.css';
import V3Head from '../site-v3/V3Head';
import './SubscriptionUI.css';

type Phase = 'checking' | 'needEmail' | 'setpw' | 'working' | 'error' | 'done';

// NOTE: Shell and card MUST live at module scope. If they're declared inside the
// component, every keystroke creates a new component identity, React remounts the
// whole form on each render, and focus jumps back to the autoFocus field (this was
// the "confirm password kicks back to password" bug).
const card: React.CSSProperties = {
  width: '100%', maxWidth: 440, background: '#fff', borderRadius: 18,
  boxShadow: '0 18px 50px rgba(20,30,60,.10)', border: '1px solid var(--line,#e7ecf3)',
  padding: '34px 30px', textAlign: 'center',
};

/**
 * Read the signup token (?d=…) from the activation URL, defensively.
 * Because /activate IS the Firebase email-action handler, on the FIRST load the token
 * is NOT at the top level — Firebase nests our whole url inside the `continueUrl` param.
 * So we look: (1) top level, (2) inside continueUrl, (3) a last-ditch scan of the href.
 */
function readPayloadTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  let token = params.get('d');
  if (!token) {
    const cont = params.get('continueUrl');
    if (cont) {
      try {
        token = new URL(cont).searchParams.get('d');
      } catch {
        const m = decodeURIComponent(cont).match(/[?&]d=([^&]+)/);
        if (m) token = decodeURIComponent(m[1]);
      }
    }
  }
  if (!token) {
    const m = window.location.href.match(/[?&]d=([^&#]+)/);
    if (m) token = decodeURIComponent(m[1]);
  }
  return token;
}

/**
 * Read the Trasse Families activation params (`family=1&org=CODE`).
 *
 * Same defensiveness as readPayloadTokenFromUrl above, and for the same reason:
 * /activate IS the Firebase email-action handler, so on the FIRST load our own
 * query is not at the top level — Firebase nests the whole URL inside
 * `continueUrl`. Miss that and a household silently falls through to the school
 * provisioning path, which has no payload and refuses.
 */
function readFamilyFromUrl(): { isFamily: boolean; orgCode: string } {
  if (typeof window === 'undefined') return { isFamily: false, orgCode: '' };
  const top = new URLSearchParams(window.location.search);
  let family = top.get('family');
  let org = top.get('org');

  if (!family || !org) {
    const cont = top.get('continueUrl');
    if (cont) {
      try {
        const inner = new URL(cont).searchParams;
        family = family || inner.get('family');
        org = org || inner.get('org');
      } catch {
        const decoded = decodeURIComponent(cont);
        const fm = decoded.match(/[?&]family=([^&]+)/);
        const om = decoded.match(/[?&]org=([^&]+)/);
        family = family || (fm ? decodeURIComponent(fm[1]) : null);
        org = org || (om ? decodeURIComponent(om[1]) : null);
      }
    }
  }
  if (!family || !org) {
    const fm = window.location.href.match(/[?&]family=([^&#]+)/);
    const om = window.location.href.match(/[?&]org=([^&#]+)/);
    family = family || (fm ? decodeURIComponent(fm[1]) : null);
    org = org || (om ? decodeURIComponent(om[1]) : null);
  }
  return { isFamily: family === '1' || family === 'true', orgCode: (org || '').trim() };
}

/** Decode the signup payload carried in the activation link, if present and well-formed. */
function payloadFromUrl(): SelfServePayload | null {
  const tok = readPayloadTokenFromUrl();
  return tok ? decodePayloadToken(tok) : null;
}

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="site-theme v3" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <V3Head />
    <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '40px 20px' }}>{children}</main>
  </div>
);

export default function PublicActivate() {
  const navigate = useNavigate();
  const { refreshAdminStatus } = useAuth();

  // Detect admin invite flow: /activate?intent=join_org&code=ORGCODE
  const urlParams = new URLSearchParams(window.location.search);
  const inviteOrgCode = urlParams.get('code') || '';
  const isInviteFlow = urlParams.get('intent') === 'join_org' && !!inviteOrgCode;

  // Trasse Families: /activate?family=1&org=OKELLO.family. The household already
  // exists (pending) and its whole setup is held server-side — there is no ?d=
  // payload to carry and nothing for the browser to provision. Setting the
  // password proves the mailbox; the backend does the rest.
  const familyParams = readFamilyFromUrl();
  const isFamilyFlow = familyParams.isFamily && !!familyParams.orgCode;
  const homePath = isFamilyFlow ? '/families' : '/fleet';

  const [phase, setPhase] = useState<Phase>('checking');
  const [error, setError] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [payload, setPayload] = useState<SelfServePayload | null>(null);
  const [email, setEmail] = useState('');
  const ranRef = useRef(false);

  // On load, validate the email link and (if we know the email) complete sign-in.
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setPhase('error');
      setError("This activation link is invalid or has expired. Start again from the pricing page and we'll send a fresh link.");
      return;
    }
    // Strongest source first: the payload carried IN the link survives any browser/account.
    const stored = loadSignup();
    const urlPayload = isInviteFlow || isFamilyFlow ? null : payloadFromUrl();
    setPayload(stored.payload ?? urlPayload);

    // We can complete sign-in automatically if we know the email — from localStorage (same
    // browser) or, crucially, from the link's own token (any browser / different Google account).
    const knownEmail = stored.email || urlPayload?.email || '';
    if (knownEmail) {
      void completeSignIn(knownEmail);
    } else {
      setPhase('needEmail');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Resolve the signup payload for an email, independent of which browser/device the
   * link is opened in. Order, most-reliable first:
   *   0. the token carried in the link itself (?d=…) — zero I/O, works cross-browser/account
   *      and is immune to ad-blockers that block Firestore. Strongest tier.
   *   1. localStorage (same browser, instant)
   *   2. POST /api/orgs/pending-activation (email from the ID token). Org list is
   *      no longer world-readable; this is the Admin-SDK path that still works
   *      in any browser once the user has proved the address.
   *   3. the signupDraft written at payment time (auth-free create; read gated by email).
   * The org query is retried once to ride out a Firestore cold-start / transient blocker.
   */
  async function resolvePayload(normalizedEmail: string): Promise<SelfServePayload | null> {
    // 0) Link-carried token. Accept it only if it matches the email the user just PROVED
    //    they own via the sign-in link — so a tampered/stale ?d= can't provision under
    //    someone else's verified address.
    const fromUrl = payloadFromUrl();
    if (fromUrl && fromUrl.email.trim().toLowerCase() === normalizedEmail) return fromUrl;

    const local = loadSignup().payload;
    if (local) return local;

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const looked = await loadPendingActivationPayload();
        if (looked.ok && looked.payload) {
          return looked.payload as unknown as SelfServePayload;
        }
        break;
      } catch {
        /* transient — retry once, then fall through to the draft */
      }
    }

    try {
      return await loadSignupDraft(normalizedEmail);
    } catch {
      return null;
    }
  }

  async function completeSignIn(addr: string) {
    setError('');
    setPhase('checking');
    try {
      await signInWithEmailLink(auth, addr, window.location.href);
      const normalizedEmail = addr.trim().toLowerCase();
      setEmail(normalizedEmail);

      const resolvedPayload = await resolvePayload(normalizedEmail);
      if (resolvedPayload) setPayload(resolvedPayload);

      setPhase('setpw');
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code;
      if (code === 'auth/invalid-email') {
        setError("That email doesn't match this activation link. Enter the work email you signed up with.");
        setPhase('needEmail');
      } else if (code === 'auth/invalid-action-code') {
        setError('This link has already been used or has expired. Start again from the pricing page for a new one.');
        setPhase('error');
      } else {
        setError((e as Error)?.message || 'Could not verify your link. Please try again.');
        setPhase('error');
      }
    }
  }

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setError('Your session expired. Please reopen the link from your email.');
      setPhase('error');
      return;
    }

    // Last-ditch resolve: if the payload didn't land at sign-in (transient Firestore
    // miss, or opened in a fresh browser), try once more now before giving up — the
    // org doc / draft is keyed by the just-verified email, so this works cross-browser.
    let effectivePayload = payload;
    if (!isInviteFlow && !isFamilyFlow && !effectivePayload) {
      effectivePayload = await resolvePayload(email);
      if (effectivePayload) setPayload(effectivePayload);
    }
    if (!isInviteFlow && !isFamilyFlow && !effectivePayload) {
      setError("We couldn't find your signup details. Please restart from the pricing page — your link is still valid, but the plan details didn't carry over.");
      return;
    }

    setPhase('working');
    try {
      await updatePassword(user, password);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === 'auth/requires-recent-login') {
        setError('For security, please reopen the link from your email and set your password right away.');
      } else {
        setError((err as Error)?.message || 'Could not set your password. Please try again.');
      }
      setPhase('setpw');
      return;
    }

    let result: { ok: boolean; error?: string };
    if (isInviteFlow) {
      result = await claimAdminInvite(user, inviteOrgCode);
    } else if (isFamilyFlow) {
      // The household's children, car, schedule and pilot are all written by the
      // backend from the server-held draft — the browser never had them.
      const activated = await activateFamilyPilot(familyParams.orgCode);
      result = activated.ok ? { ok: true } : { ok: false, error: activated.message };
    } else {
      result = await provisionSelfServeOrg(user, effectivePayload!);
    }
    if (!result.ok) {
      setError(result.error || 'Could not finish setting up your account.');
      setPhase('setpw');
      return;
    }

    await refreshAdminStatus();
    clearSignup();
    void clearSignupDraft(email);
    customToast.show(
      isFamilyFlow
        ? 'Welcome to Trasse Families — your free days start now!'
        : 'Welcome to trasse — your dashboard is ready!',
      'success',
    );
    setPhase('done');
    // Give the auth context a tick to propagate the new fleet-admin role, then enter.
    setTimeout(() => navigate(homePath, { replace: true }), 1400);
  }

  if (phase === 'checking' || phase === 'working') {
    return (
      <Shell>
        <div style={card}>
          <FaSpinner className="spin" style={{ fontSize: 30, color: 'var(--brand-ink,#2f7cf0)' }} />
          <h2 style={{ margin: '16px 0 6px' }}>{phase === 'working' ? 'Setting up your dashboard…' : 'Verifying your link…'}</h2>
          <p style={{ color: 'var(--ink-2,#5b6b82)', margin: 0 }}>One moment.</p>
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <div style={card}>
          <div style={{ fontSize: 28 }}>⚠️</div>
          <h2 style={{ margin: '12px 0 6px' }}>Link problem</h2>
          <p style={{ color: 'var(--ink-2,#5b6b82)' }}>{error}</p>
          <Link className="btn btn-primary btn-lg" to="/pricing" style={{ marginTop: 10 }}>Back to pricing</Link>
        </div>
      </Shell>
    );
  }

  if (phase === 'done') {
    return (
      <Shell>
        <div style={card}>
          <div className="done-mark" style={{ margin: '0 auto 14px' }}><FaCheck /></div>
          <h2 style={{ margin: '0 0 6px' }}>You're all set</h2>
          <p style={{ color: 'var(--ink-2,#5b6b82)' }}>
            {isFamilyFlow ? 'Taking you to your household…' : 'Taking you to your Bus Tracking dashboard…'}
          </p>
          <button className="btn btn-primary btn-lg" style={{ marginTop: 10 }} onClick={() => navigate(homePath, { replace: true })}>
            {isFamilyFlow ? 'Enter my household' : 'Enter dashboard'}
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'needEmail') {
    return (
      <Shell>
        <div style={card}>
          <FaEnvelopeOpenText style={{ fontSize: 26, color: 'var(--brand-ink,#2f7cf0)' }} />
          <h2 style={{ margin: '14px 0 6px' }}>Confirm your email</h2>
          <p style={{ color: 'var(--ink-2,#5b6b82)' }}>
            {isInviteFlow
              ? 'For security, type the exact email address that received this invite.'
              : isFamilyFlow
                ? 'For your security, type the email address you signed up with.'
                : 'For your security, type the work email you used to sign up.'}
          </p>
          <form onSubmit={(e) => { e.preventDefault(); if (emailInput.trim()) void completeSignIn(emailInput.trim().toLowerCase()); }}>
            <input
              type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
              placeholder={isFamilyFlow ? 'you@example.com' : 'you@organization.co.ke'}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--line,#e7ecf3)', fontSize: 15, marginBottom: 12 }}
            />
            {error && <div style={{ color: '#d3455b', fontSize: 13.5, marginBottom: 10 }}>{error}</div>}
            <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }} disabled={!emailInput.trim()}>Continue</button>
          </form>
        </div>
      </Shell>
    );
  }

  // phase === 'setpw'
  return (
    <Shell>
      <div style={card}>
        <FaLock style={{ fontSize: 24, color: 'var(--brand-ink,#2f7cf0)' }} />
        <h2 style={{ margin: '14px 0 4px' }}>{isInviteFlow ? 'Accept your admin invite' : 'Create your password'}</h2>
        <p style={{ color: 'var(--ink-2,#5b6b82)', marginTop: 0 }}>
          {isInviteFlow
            ? <>Set a password to join the fleet admin dashboard.</>
            : isFamilyFlow
              ? <>This is what starts your free days — your household is <b>{familyParams.orgCode}</b>.</>
              : payload?.orgName ? <>Admin login for <b>{payload.orgName}</b>.</> : 'Set a password to finish.'}
        </p>

        <form onSubmit={handleSetPassword} style={{ textAlign: 'left', marginTop: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2,#5b6b82)' }}>Work email</label>
          <input
            value={email} readOnly
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--line,#e7ecf3)', background: '#f4f7fb', color: 'var(--ink-2,#5b6b82)', fontSize: 15, margin: '6px 0 14px' }}
          />
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2,#5b6b82)' }}>Password</label>
          <div style={{ position: 'relative', margin: '6px 0 12px' }}>
            <input
              type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters" autoFocus
              style={{ width: '100%', padding: '12px 42px 12px 14px', borderRadius: 10, border: '1px solid var(--line,#e7ecf3)', fontSize: 15 }}
            />
            <button type="button" onClick={() => setShowPw((s) => !s)} aria-label="toggle password"
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3,#8a99ad)' }}>
              {showPw ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2,#5b6b82)' }}>Confirm password</label>
          <input
            type={showPw ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter password"
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--line,#e7ecf3)', fontSize: 15, margin: '6px 0 14px' }}
          />
          {error && <div style={{ color: '#d3455b', fontSize: 13.5, marginBottom: 10 }}>{error}</div>}
          <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }}>Create password & enter <FaCheck /></button>
        </form>
      </div>
    </Shell>
  );
}
